import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import { useState } from "react";
import type { EmailWithError } from "app/pages/digital-suite/home-page/components/Rewards/screens/MainScreen/sections/InviteSection/utils/consts";

interface UseChipsInputProps {
    emailChips: string[];
    setEmailChips: Dispatch<SetStateAction<string[]>>;
    invalidEmails: EmailWithError[];
    setInvalidEmails: Dispatch<SetStateAction<EmailWithError[]>>;
    inputRef: MutableRefObject<any>;
}
export const useChipsInput = ({
    emailChips,
    setEmailChips,
    inputRef,
    invalidEmails,
    setInvalidEmails,
}: UseChipsInputProps) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isContainerClicked, setIsContainerClicked] = useState(false);

    const isChipsEmpty = emailChips.length === 0;
    const shouldShowTextField = isFocused || isChipsEmpty || inputRef?.current?.getValue?.().trim() !== "";

    const addChip = (value: string) => {
        const trimmedValue = value?.trim();
        if (!trimmedValue) {
            return;
        }

        const lowerCaseValue = trimmedValue.toLowerCase();
        const isDuplicate = emailChips.some((chip) => chip.toLowerCase() === lowerCaseValue);

        if (!isDuplicate) {
            setEmailChips((prev) => [...prev, trimmedValue]);
        }

        inputRef?.current?.clearValue();
    };

    const handleInputChange = (value: string) => {
        if (value.endsWith(" ")) {
            addChip(value);
            return;
        }

        inputRef?.current?.setValue(value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            addChip(inputRef?.current?.getValue?.());
        }
    };

    const handleChipRemove = (index: number, chip: string) => {
        setInvalidEmails((prev) => prev.filter((item) => item.email !== chip));
        setEmailChips((prev) => prev.filter((_, i) => i !== index));
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const pasteData = e.clipboardData.getData("text");
        if (!pasteData.includes(",") && !pasteData.includes("\n")) {
            return;
        }

        e.preventDefault();

        const pastedChips = pasteData
            .split(/[,\n]/)
            .map((item: string) => item.trim())
            .filter((item: string) => item.length > 0);

        const uniqueNewChips = pastedChips.filter((newChip) => {
            const lowerChip = newChip.toLowerCase();
            return !emailChips.some((existingChip) => existingChip.toLowerCase() === lowerChip);
        });

        if (uniqueNewChips.length) {
            setEmailChips((prev) => [...prev, ...uniqueNewChips]);
        }
    };

    const isEmailInvalid = (email: string): boolean => {
        return invalidEmails.some((item) => item.email === email);
    };

    const getTooltipContent = (email: string): string => {
        if (!isEmailInvalid(email)) {
            return "";
        }

        const invalidEmail = invalidEmails.find((item) => item.email === email);
        return invalidEmail?.reason || "Invalid email format";
    };

    const handleFocus = () => setIsFocused(true);

    const handleContainerClick = () => {
        setIsFocused(true);
        inputRef?.current?.focus();
    };

    const handleBlur = () => {
        if (!isContainerClicked) {
            setIsFocused(false);
        }
    };

    const handleMouseDown = () => setIsContainerClicked(true);

    const handleMouseUp = () => setIsContainerClicked(false);

    return {
        isFocused,
        isChipsEmpty,
        shouldShowTextField,
        handleInputChange,
        handleKeyDown,
        handleChipRemove,
        handlePaste,
        isEmailInvalid,
        getTooltipContent,
        handleFocus,
        handleContainerClick,
        handleBlur,
        handleMouseDown,
        handleMouseUp,
    };
};
